<template>
    <div class="llegada">

        <!-- Cargando -->
        <HyFLoader v-if="loading" variant="inline" />

        <!-- Link inválido -->
        <div v-else-if="error" class="lg-state">
            <h1 class="lg-state__title">{{ $t('arrival.error-title') }}</h1>
            <p>{{ error }}</p>
            <p class="lg-state__hint">{{ $t('arrival.error-hint') }}</p>
        </div>

        <div v-else class="lg-wrapper">

            <!-- Encabezado -->
            <div class="lg-head">
                <p class="lg-eyebrow">
                    {{ $t(isGuest ? 'arrival.eyebrow-guest' : 'arrival.eyebrow-host') }}
                </p>
                <h1 class="lg-title">
                    {{ $t(isGuest ? 'arrival.title-guest' : 'arrival.title-host') }}
                </h1>
                <p class="lg-sub">
                    {{ $t(isGuest ? 'arrival.sub-guest' : 'arrival.sub-host') }}
                </p>
            </div>

            <!-- Resumen de la reserva -->
            <div class="lg-summary">
                <div v-if="reservation.accommodationId" class="lg-summary__row">
                    <span class="lg-summary__label">{{ $t('arrival.summary-accommodation') }}</span>
                    <router-link
                        class="lg-summary__link"
                        :to="'/alojamiento/' + reservation.accommodationId"
                        target="_blank"
                    >
                        #{{ reservation.accommodationId }}
                    </router-link>
                </div>
                <div v-if="reservation.accommodationAddress" class="lg-summary__row">
                    <span class="lg-summary__label">{{ $t('arrival.summary-address') }}</span>
                    <span class="lg-summary__value">{{ reservation.accommodationAddress }}</span>
                </div>
                <div class="lg-summary__row">
                    <span class="lg-summary__label">{{ $t('arrival.summary-checkin') }}</span>
                    <span class="lg-summary__value">{{ reservation.checkIn }}</span>
                </div>
                <div v-if="!isGuest && reservation.guestName" class="lg-summary__row">
                    <span class="lg-summary__label">{{ $t('arrival.summary-guest') }}</span>
                    <span class="lg-summary__value">{{ reservation.guestName }}</span>
                </div>
            </div>

            <!-- El formulario se completa una sola vez -->
            <div v-if="alreadyCompleted" class="lg-card lg-closed">
                <div class="lg-closed__icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#1D9E75" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 class="lg-closed__title">{{ $t('arrival.done-title') }}</h2>
                <p class="lg-closed__text">
                    {{ $t(isGuest ? 'arrival.done-guest' : 'arrival.done-host') }}
                </p>
            </div>

            <!-- ───────────── Formulario del huésped ───────────── -->
            <form v-else-if="isGuest" class="lg-card" @submit.prevent="submit">

                <div class="lg-field">
                    <label class="lg-label">{{ $t('arrival.motive-label') }}</label>
                    <div class="lg-radio-group">
                        <label
                            v-for="opt in motivos"
                            :key="opt.value"
                            class="lg-radio"
                            :class="{ 'lg-radio--selected': guest.motivo === opt.value }"
                        >
                            <input type="radio" :value="opt.value" v-model="guest.motivo" /> {{ opt.label }}
                        </label>
                    </div>
                    <span v-if="errors.motivo" class="lg-error">{{ errors.motivo }}</span>
                </div>

                <div v-if="guest.motivo" class="lg-field">
                    <label class="lg-label">{{ institucionLabel }}</label>
                    <input class="lg-input" v-model="guest.institucion" :placeholder="institucionPlaceholder" />
                    <span v-if="errors.institucion" class="lg-error">{{ errors.institucion }}</span>
                </div>

                <div class="lg-field">
                    <label class="lg-label">{{ $t('arrival.flight-label') }}</label>
                    <div class="lg-radio-group">
                        <label class="lg-radio" :class="{ 'lg-radio--selected': guest.tieneVuelo === true }">
                            <input type="radio" :value="true" v-model="guest.tieneVuelo" /> {{ $t('arrival.flight-yes') }}
                        </label>
                        <label class="lg-radio" :class="{ 'lg-radio--selected': guest.tieneVuelo === false }">
                            <input type="radio" :value="false" v-model="guest.tieneVuelo" /> {{ $t('arrival.flight-no') }}
                        </label>
                    </div>
                    <span v-if="errors.tieneVuelo" class="lg-error">{{ errors.tieneVuelo }}</span>
                </div>

                <!-- Sin pasajes: mensaje automático, no se piden horarios -->
                <div v-if="guest.tieneVuelo === false" class="lg-callout">
                    <strong>{{ $t('arrival.flight-pending-strong') }}</strong>
                    {{ $t('arrival.flight-pending-text') }}
                </div>

                <!-- Con pasajes -->
                <template v-if="guest.tieneVuelo === true">
                    <div class="lg-field">
                        <label class="lg-label">{{ $t('arrival.flight') }}</label>
                        <input class="lg-input" v-model="guest.vuelo" :placeholder="$t('arrival.flight-ph')" />
                        <span v-if="errors.vuelo" class="lg-error">{{ errors.vuelo }}</span>
                    </div>

                    <div class="lg-field">
                        <label class="lg-label">{{ $t('arrival.arrival-date') }}</label>
                        <input class="lg-input" type="date" v-model="guest.fechaLlegada" />
                        <span v-if="errors.fechaLlegada" class="lg-error">{{ errors.fechaLlegada }}</span>
                    </div>

                    <div class="lg-field">
                        <label class="lg-label">{{ $t('arrival.arrival-time') }}</label>
                        <p class="lg-hint lg-hint--block">{{ $t('arrival.arrival-time-hint') }}</p>
                        <div class="lg-row">
                            <div class="lg-field lg-field--inline">
                                <label class="lg-label lg-label--small">{{ $t('arrival.from') }}</label>
                                <input class="lg-input" type="time" v-model="guest.horaDesde" />
                            </div>
                            <div class="lg-field lg-field--inline">
                                <label class="lg-label lg-label--small">{{ $t('arrival.to') }}</label>
                                <input class="lg-input" type="time" v-model="guest.horaHasta" />
                            </div>
                        </div>

                        <!-- El rango se interpreta en la hora del país de destino,
                             no en la del país desde el que se completa el formulario. -->
                        <p class="lg-note">{{ $t('arrival.local-time-note') }}</p>

                        <span v-if="errors.hora" class="lg-error">{{ errors.hora }}</span>
                    </div>
                </template>

                <div v-if="submitError" class="lg-error lg-error--block">{{ submitError }}</div>

                <div class="lg-actions">
                    <button class="lg-btn" type="submit" :disabled="sending">
                        <span v-if="sending" class="lg-spinner lg-spinner--btn"></span>
                        {{ sending ? $t('arrival.saving') : $t('arrival.save') }}
                    </button>
                </div>
            </form>

            <!-- ───────────── Formulario del anfitrión ───────────── -->
            <form v-else-if="!isGuest" class="lg-card" @submit.prevent="submit">

                <div class="lg-field">
                    <label class="lg-label">{{ $t('arrival.who-receives') }}</label>
                    <input class="lg-input" v-model="host.recibe" :placeholder="$t('arrival.who-receives-ph')" />
                    <span v-if="errors.recibe" class="lg-error">{{ errors.recibe }}</span>
                </div>

                <div class="lg-field">
                    <label class="lg-label">{{ $t('arrival.phone') }}</label>
                    <input class="lg-input" v-model="host.telefono" :placeholder="$t('arrival.phone-ph')" />
                    <span v-if="errors.telefono" class="lg-error">{{ errors.telefono }}</span>
                </div>

                <div class="lg-field">
                    <label class="lg-label">{{ $t('arrival.access-label') }}</label>
                    <div class="lg-radio-group">
                        <label
                            v-for="opt in accesos"
                            :key="opt.value"
                            class="lg-radio"
                            :class="{ 'lg-radio--selected': host.acceso === opt.value }"
                        >
                            <input type="radio" :value="opt.value" v-model="host.acceso" /> {{ opt.label }}
                        </label>
                    </div>
                    <span v-if="errors.acceso" class="lg-error">{{ errors.acceso }}</span>
                </div>

                <div v-if="host.acceso" class="lg-field">
                    <label class="lg-label">{{ $t('arrival.access-detail') }}</label>
                    <input class="lg-input" v-model="host.accesoDetalle" :placeholder="accesoPlaceholder" />
                    <p class="lg-hint lg-hint--block">{{ accesoAyuda }}</p>
                    <span v-if="errors.accesoDetalle" class="lg-error">{{ errors.accesoDetalle }}</span>
                </div>

                <div class="lg-field">
                    <label class="lg-label">{{ $t('arrival.place-detail') }}</label>
                    <textarea
                        class="lg-textarea"
                        rows="4"
                        v-model="host.detalle"
                        :placeholder="$t('arrival.place-detail-ph')"
                    ></textarea>
                    <p class="lg-hint lg-hint--block">{{ $t('arrival.place-detail-hint') }}</p>
                    <span v-if="errors.detalle" class="lg-error">{{ errors.detalle }}</span>
                </div>

                <div v-if="submitError" class="lg-error lg-error--block">{{ submitError }}</div>

                <div class="lg-actions">
                    <button class="lg-btn" type="submit" :disabled="sending">
                        <span v-if="sending" class="lg-spinner lg-spinner--btn"></span>
                        {{ sending ? $t('arrival.saving') : $t('arrival.save') }}
                    </button>
                </div>
            </form>

        </div>

        <!-- Pop up de éxito -->
        <div v-if="success" class="lg-modal">
            <div class="lg-modal__backdrop" @click="salir"></div>
            <div class="lg-modal__content">
                <div class="lg-modal__icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#1D9E75" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 class="lg-modal__title">{{ $t('arrival.success-title') }}</h2>
                <p class="lg-modal__text">{{ successText }}</p>
                <button class="lg-btn lg-btn--modal" @click="salir">
                    {{ $t(estaLogueado ? 'arrival.success-profile' : 'arrival.success-login') }}
                </button>
                <p class="lg-modal__hint">{{ $t('arrival.success-redirect') }}</p>
            </div>
        </div>

    </div>
</template>

<script>
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import axios from 'axios';

/**
 * Formulario de llegada (check-in) de una reserva aceptada.
 *
 * Es una sola landing para las dos partes: el token del link dice si el que
 * entra es el huésped o el anfitrión, y de eso depende qué formulario se pinta.
 * No pide login: el link viaja en el correo de reserva aceptada.
 *
 * Cuando los dos completan su mitad, la API le manda al huésped el correo con
 * los datos del check-in. Acá solo mostramos el pop up de éxito.
 */
export default {
    components: { HyFLoader },
    name: 'LlegadaCheckin',

    props: {
        // Respuesta del endpoint que ya trajo `Llegada.vue`. Si viene, no se
        // vuelve a consultar; si no, el componente se arregla solo.
        precargado: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            sending: false,
            error: null,
            submitError: null,
            success: false,
            successText: '',
            redirectTimer: null,

            audience: null,
            alreadyCompleted: false,
            reservation: {},

            guest: {
                motivo: null,
                institucion: '',
                tieneVuelo: null,
                vuelo: '',
                fechaLlegada: '',
                horaDesde: '',
                horaHasta: '',
            },

            host: {
                recibe: '',
                telefono: '',
                acceso: null,
                accesoDetalle: '',
                detalle: '',
            },

            errors: {},
        };
    },

    computed: {
        // Van en computed y no en data() para que se retraduzcan al vuelo
        // cuando se cambia de idioma sin recargar la página.
        motivos() {
            return [
                { value: 'universidad', label: this.$t('arrival.motive-university') },
                { value: 'trabajo', label: this.$t('arrival.motive-work') },
                { value: 'otro', label: this.$t('arrival.motive-other') },
            ];
        },

        accesos() {
            return [
                { value: 'anfitrion', label: this.$t('arrival.access-host') },
                { value: 'codigo', label: this.$t('arrival.access-code') },
                { value: 'conserjeria', label: this.$t('arrival.access-concierge') },
                { value: 'otro', label: this.$t('arrival.access-other') },
            ];
        },

        isGuest() {
            return this.audience === 'guest';
        },
        estaLogueado() {
            return this.$store.getters.isLoggedIn;
        },
        idreserva() {
            return this.$route.params.idreserva;
        },
        token() {
            return this.$route.params.token;
        },
        institucionLabel() {
            if (this.guest.motivo === 'universidad') return this.$t('arrival.institution-university');
            if (this.guest.motivo === 'trabajo') return this.$t('arrival.institution-work');
            return this.$t('arrival.institution-other');
        },
        institucionPlaceholder() {
            if (this.guest.motivo === 'universidad') return this.$t('arrival.institution-ph-university');
            if (this.guest.motivo === 'trabajo') return this.$t('arrival.institution-ph-work');
            return this.$t('arrival.institution-ph-other');
        },
        accesoPlaceholder() {
            const ejemplos = {
                anfitrion: 'arrival.access-ph-host',
                codigo: 'arrival.access-ph-code',
                conserjeria: 'arrival.access-ph-concierge',
                otro: 'arrival.access-ph-other',
            };
            return ejemplos[this.host.acceso] ? this.$t(ejemplos[this.host.acceso]) : '';
        },
        accesoAyuda() {
            const ayudas = {
                anfitrion: 'arrival.access-help-host',
                codigo: 'arrival.access-help-code',
                conserjeria: 'arrival.access-help-concierge',
                otro: 'arrival.access-help-other',
            };
            return ayudas[this.host.acceso] ? this.$t(ayudas[this.host.acceso]) : '';
        },
    },

    created() {
        this.cargar();
    },

    methods: {
        cargar() {
            // `Llegada.vue` ya consultó el endpoint para saber qué formulario
            // mostrar, así que reaprovecha esa respuesta en vez de pedirla otra vez.
            if (this.precargado) {
                this.aplicar(this.precargado);
                this.loading = false;
                return;
            }

            axios
                .get(`${process.env.VUE_APP_API_URL}arrival/${this.idreserva}/${this.token}`)
                .then(({ data }) => {
                    this.aplicar(data);
                })
                .catch(() => {
                    this.error = this.$t('arrival.error-invalid');
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        aplicar(data) {
            this.audience = data.audience;
            this.reservation = data.reservation || {};
            this.alreadyCompleted = !!data.completed;

            // Lo ya cargado vuelve al formulario para que puedan corregirlo.
            if (data.data) {
                const target = this.isGuest ? this.guest : this.host;
                Object.keys(target).forEach((key) => {
                    if (data.data[key] !== undefined && data.data[key] !== null) {
                        target[key] = data.data[key];
                    }
                });
            }
        },

        validar() {
            this.errors = {};

            if (this.isGuest) {
                if (!this.guest.motivo) {
                    this.$set(this.errors, 'motivo', this.$t('arrival.err-motive'));
                }
                if (this.guest.motivo && !String(this.guest.institucion || '').trim()) {
                    this.$set(
                        this.errors,
                        'institucion',
                        this.guest.motivo === 'otro'
                            ? this.$t('arrival.err-institution-other')
                            : this.$t('arrival.err-institution')
                    );
                }
                if (this.guest.tieneVuelo === null) {
                    this.$set(this.errors, 'tieneVuelo', this.$t('arrival.err-flight-choice'));
                }
                if (this.guest.tieneVuelo === true) {
                    if (!this.guest.vuelo) {
                        this.$set(this.errors, 'vuelo', this.$t('arrival.err-flight'));
                    }
                    if (!this.guest.fechaLlegada) {
                        this.$set(this.errors, 'fechaLlegada', this.$t('arrival.err-date'));
                    }
                    if (!this.guest.horaDesde || !this.guest.horaHasta) {
                        this.$set(this.errors, 'hora', this.$t('arrival.err-time'));
                    } else {
                        const desde = this.enMinutos(this.guest.horaDesde);
                        const hasta = this.enMinutos(this.guest.horaHasta);

                        if (hasta <= desde) {
                            this.$set(this.errors, 'hora', this.$t('arrival.err-time-order'));
                        } else if (hasta - desde > 120) {
                            this.$set(this.errors, 'hora', this.$t('arrival.err-time-range'));
                        }
                    }
                }
            } else {
                if (!this.host.recibe) {
                    this.$set(this.errors, 'recibe', this.$t('arrival.err-receives'));
                }
                if (!this.host.telefono) {
                    this.$set(this.errors, 'telefono', this.$t('arrival.err-phone'));
                }
                if (!this.host.acceso) {
                    this.$set(this.errors, 'acceso', this.$t('arrival.err-access'));
                }
                if (this.host.acceso && !String(this.host.accesoDetalle || '').trim()) {
                    this.$set(
                        this.errors,
                        'accesoDetalle',
                        this.host.acceso === 'otro'
                            ? this.$t('arrival.err-access-other')
                            : this.$t('arrival.err-access-detail')
                    );
                }
                if (!String(this.host.detalle || '').trim()) {
                    this.$set(this.errors, 'detalle', this.$t('arrival.err-place'));
                }
            }

            return Object.keys(this.errors).length === 0;
        },

        enMinutos(hora) {
            const [h, m] = String(hora).split(':');
            return (parseInt(h, 10) * 60) + parseInt(m, 10);
        },

        submit() {
            this.submitError = null;

            if (!this.validar()) {
                return;
            }

            this.sending = true;

            const base = `${process.env.VUE_APP_API_URL}arrival/${this.idreserva}/${this.token}`;
            const url = this.isGuest ? `${base}/guest` : `${base}/host`;
            const payload = this.isGuest
                ? {
                    motivo: this.guest.motivo,
                    institucion: this.guest.institucion || null,
                    tieneVuelo: this.guest.tieneVuelo ? 1 : 0,
                    vuelo: this.guest.tieneVuelo ? this.guest.vuelo : null,
                    fechaLlegada: this.guest.tieneVuelo ? this.guest.fechaLlegada : null,
                    horaDesde: this.guest.tieneVuelo ? this.guest.horaDesde : null,
                    horaHasta: this.guest.tieneVuelo ? this.guest.horaHasta : null,
                }
                : {
                    recibe: this.host.recibe,
                    telefono: this.host.telefono,
                    acceso: this.host.acceso,
                    accesoDetalle: this.host.accesoDetalle || null,
                    detalle: this.host.detalle || null,
                };

            axios
                .post(url, payload)
                .then(({ data }) => {
                    this.alreadyCompleted = true;
                    this.successText = this.textoDeExito(data);
                    this.success = true;

                    // El pop up se ve unos segundos y después seguimos al perfil
                    // (o al login, si entró desde el mail sin sesión).
                    this.redirectTimer = setTimeout(this.salir, 4000);
                })
                .catch((error) => {
                    const status = error.response && error.response.status;

                    if (status === 409) {
                        this.alreadyCompleted = true;
                        this.submitError = null;
                    } else if (status === 422) {
                        this.submitError = this.$t('arrival.err-submit-422');
                    } else {
                        this.submitError = this.$t('arrival.err-submit');
                    }
                })
                .finally(() => {
                    this.sending = false;
                });
        },

        textoDeExito(data) {
            if (!this.isGuest) {
                return this.$t('arrival.success-host');
            }

            if (data && data.pendingFlight) {
                return this.$t('arrival.success-guest-pending');
            }

            return this.$t('arrival.success-guest');
        },

        /**
         * Después de guardar seguimos al perfil. Si el link se abrió desde el
         * correo sin sesión iniciada, va al login.
         */
        salir() {
            if (this.redirectTimer) {
                clearTimeout(this.redirectTimer);
                this.redirectTimer = null;
            }

            const destino = this.$store.getters.isLoggedIn
                ? { name: 'editar-perfil' }
                : { name: 'login' };

            this.$router.push(destino).catch(() => {});
        },
    },

    beforeDestroy() {
        if (this.redirectTimer) {
            clearTimeout(this.redirectTimer);
        }
    },
};
</script>

<style scoped lang="scss">
.llegada {
    background: #fbfaf8;
    min-height: 70vh;
    padding: 2.5rem 1rem 3.5rem;
    /* DefaultLayout reserva la altura del header en .main-content, pero el header
       es sticky y ya ocupa su lugar en el flujo: ese padding dejaba una franja
       blanca sobre el formulario. */
    margin-top: -$mobile-header-height;

    @media (min-width: $md) {
        margin-top: -$desktop-header-height;
    }
}

.lg-wrapper {
    max-width: 640px;
    margin: 0 auto;
}

// ── Estados ────────────────────────────────────────────────────────────────

.lg-state {
    max-width: 480px;
    margin: 0 auto;
    text-align: center;
    color: #5f5e5a;
    font-size: 14px;

    &__title {
        font-size: 20px;
        font-weight: 600;
        color: #085041;
        margin-bottom: 10px;
    }

    &__hint {
        font-size: 13px;
        color: #888780;
        margin-top: 10px;
    }
}

// ── Encabezado ─────────────────────────────────────────────────────────────

.lg-eyebrow {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #ba7517;
    margin-bottom: 8px;
}

.lg-title {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a18;
    margin: 0 0 6px 0;
}

.lg-sub {
    font-size: 14px;
    color: #5f5e5a;
    margin: 0 0 1.5rem 0;
}

// ── Resumen ────────────────────────────────────────────────────────────────

.lg-summary {
    background: #fff7f1;
    border: 1px solid #f3ddcb;
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 1.25rem;

    &__row {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        padding: 5px 0;
        font-size: 13px;
    }

    &__label {
        color: #8a6a52;
        flex-shrink: 0;
    }

    &__value {
        color: #1a1a18;
        font-weight: 500;
        text-align: right;
    }

    &__link {
        color: #ed6b16;
        font-weight: 600;
        text-decoration: none;
        text-align: right;

        &:hover {
            text-decoration: underline;
        }
    }
}

// ── Formulario ya completado ───────────────────────────────────────────────

.lg-closed {
    text-align: center;

    &__icon {
        width: 52px;
        height: 52px;
        border-radius: 50%;
        background: #eaf7f1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 14px;
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        color: #1a1a18;
        margin: 0 0 8px 0;
    }

    &__text {
        font-size: 14px;
        color: #5f5e5a;
        line-height: 1.55;
        margin: 0;
    }
}

// ── Formulario ─────────────────────────────────────────────────────────────

.lg-card {
    background: #fff;
    border: 1px solid #e6e4df;
    border-radius: 14px;
    padding: 1.75rem 1.5rem;
}

.lg-field {
    margin-bottom: 1.25rem;

    &--inline {
        flex: 1;
        margin-bottom: 0;
    }
}

.lg-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #1a1a18;
    margin-bottom: 8px;

    &--small {
        font-size: 12px;
        font-weight: 400;
        color: #5f5e5a;
    }
}

.lg-hint {
    font-size: 12px;
    color: #888780;
    font-weight: 400;

    &--block {
        display: block;
        margin: 6px 0 0 0;
    }
}

/* Aviso en negrita: se distingue de .lg-hint, que es gris y secundario. */
.lg-note {
    display: block;
    margin: 10px 0 0 0;
    font-size: 13px;
    font-weight: 700;
    color: #1a1a18;
}

.lg-input,
.lg-textarea {
    width: 100%;
    border: 1px solid #d6d5d0;
    border-radius: 8px;
    padding: 0 12px;
    height: 42px;
    font-size: 14px;
    color: #1a1a18;
    background: #fff;
    outline: none;
    transition: border-color 0.12s;

    &:focus {
        border-color: #ed6b16;
    }
}

.lg-textarea {
    height: auto;
    padding: 10px 12px;
    resize: vertical;
    line-height: 1.5;
}

.lg-row {
    display: flex;
    gap: 12px;
}

.lg-radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.lg-radio {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 9px 16px;
    border: 1px solid #d6d5d0;
    border-radius: 999px;
    cursor: pointer;
    font-size: 13px;
    color: #5f5e5a;
    margin: 0;
    transition: border-color 0.12s, background 0.12s;

    input {
        display: none;
    }

    &--selected {
        border-color: #ed6b16;
        background: #fff4ec;
        color: #b4530d;
        font-weight: 500;
    }
}

.lg-callout {
    background: #fff4ec;
    border-left: 4px solid #ed6b16;
    border-radius: 6px;
    padding: 14px 16px;
    font-size: 13px;
    color: #4a4a4a;
    line-height: 1.55;
    margin-bottom: 1.25rem;

    strong {
        display: block;
        color: #b4530d;
        margin-bottom: 4px;
    }
}

.lg-error {
    display: block;
    font-size: 12px;
    color: #d85a30;
    margin-top: 6px;

    &--block {
        margin: 0 0 1rem 0;
        padding: 10px 14px;
        background: #fff4f0;
        border: 1px solid #d85a30;
        border-radius: 8px;
    }
}

.lg-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

.lg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 44px;
    padding: 0 28px;
    border: none;
    border-radius: 999px;
    background: #ed6b16;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    &--modal {
        width: 100%;
        margin-top: 18px;
    }
}

// ── Spinner ────────────────────────────────────────────────────────────────

.lg-spinner {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(237, 107, 22, 0.25);
    border-top-color: #ed6b16;
    border-radius: 50%;
    animation: lg-spin 0.7s linear infinite;
    margin: 0 auto 10px;

    &--btn {
        width: 14px;
        height: 14px;
        border-color: rgba(255, 255, 255, 0.45);
        border-top-color: #fff;
        margin: 0;
    }
}

@keyframes lg-spin {
    to { transform: rotate(360deg); }
}

// ── Pop up de éxito ────────────────────────────────────────────────────────

.lg-modal {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;

    &__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.55);
    }

    &__content {
        position: relative;
        z-index: 2;
        background: #fff;
        border-radius: 16px;
        padding: 2rem 1.75rem 1.75rem;
        max-width: 420px;
        width: 100%;
        text-align: center;
        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    }

    &__icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #eaf7f1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 14px;
    }

    &__title {
        font-size: 20px;
        font-weight: 600;
        color: #1a1a18;
        margin: 0 0 8px 0;
    }

    &__text {
        font-size: 14px;
        color: #5f5e5a;
        line-height: 1.55;
        margin: 0;
    }

    &__hint {
        font-size: 12px;
        color: #888780;
        margin: 12px 0 0 0;
    }
}

@media (max-width: 520px) {
    .lg-card { padding: 1.25rem 1rem; }
    .lg-row { flex-direction: column; gap: 1rem; }
    .lg-btn { width: 100%; }
    .lg-actions { justify-content: stretch; }
}
</style>
